<template>
  <div class="admin-container">
    <!-- Loading Overlay -->
    <LoadingOverlay
      v-model="isInitialLoading"
      message="Loading lesson plan..."
      subtitle="Please wait while we fetch your lesson content"
      v-bind="loadingOverlayProps"
    />

    <div class="admin-header">
      <h1>Edit Lesson Plan</h1>
      <div class="action-buttons">
        <button
          class="preview-button"
          @click="showPreviewModal"
          :disabled="isSubmitting"
        >
          <div
            class="spinner"
            v-if="isSubmitting && savingType === 'preview'"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          {{
            isSubmitting && savingType === "preview"
              ? "Saving & Previewing..."
              : "Preview"
          }}
        </button>
        <button
          class="draft-button"
          @click="saveLesson('draft')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'draft'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "draft"
              ? "Saving..."
              : "Save as Draft"
          }}
        </button>
        <button
          class="publish-button"
          @click="saveLesson('published')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'published'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "published"
              ? "Publishing..."
              : "Save as Published"
          }}
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="showValidationErrors" class="validation-errors">
      <div class="error-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h3>Please fix the following errors:</h3>
        <button class="close-error" @click="showValidationErrors = false">
          ×
        </button>
      </div>
      <ul>
        <li v-for="(error, field) in validationErrors" :key="field">
          {{ error }}
        </li>
      </ul>
    </div>

    <!-- Preview Confirmation Modal -->
    <ConfirmationModal
      v-model="showPreviewConfirm"
      title="Save and Preview Lesson Plan"
      message="This will save your current changes and open the lesson in preview mode. Do you want to continue?"
      confirm-text="Save & Preview"
      cancel-text="Cancel"
      :loading="isSubmitting && savingType === 'preview'"
      @confirm="confirmPreview"
      @cancel="closePreviewModal"
    />

    <div class="admin-content-layout">
      <div class="admin-main-content">
        <TitleSection
          ref="titleSectionRef"
          @update:title="updateTitle"
          @update:subtitle="updateSubtitle"
          @update:duration="updateDuration"
        />
        <LessonPlanEditorSection
          ref="editorRef"
          @tabs-updated="updateTabs"
          :is-edit-form="true"
        />
      </div>
      <div
        class="admin-right-sidebar"
        :class="{ 'mobile-open': rightSidebarOpen }"
      >
        <div class="admin-sidebar-header">
          <button
            class="close-sidebar-button"
            @click="toggleRightSidebar"
            v-if="isMobile"
          >
            Close Sidebar
          </button>
        </div>
        <div class="admin-sidebar-content">
          <!-- Sidebar content here -->
          <RightSidebar
            ref="rightSidebarRef"
            @cover-image-updated="updateCoverImage"
            @tag-sections-updated="updateTagSections"
            @permalink-updated="updatePermalink"
          />
        </div>
      </div>
    </div>

    <!-- Snackbar Notification -->
    <div v-if="notification.show" class="snackbar" :class="notification.type">
      <div class="snackbar-content">
        <svg
          v-if="notification.type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ notification.message }}</span>
      </div>
      <button class="snackbar-close" @click="closeNotification">×</button>
    </div>

    <button
      class="floating-button"
      @click="toggleRightSidebar"
      v-if="isMobile && !rightSidebarOpen"
    >
      Open Sidebar
    </button>
    <div
      class="right-sidebar-overlay"
      v-if="rightSidebarOpen && isMobile"
      @click="toggleRightSidebar"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import LessonPlanEditorSection from "@/components/admin/lesson-plans/LessonPlanEditorSection.vue";
import RightSidebar from "@/components/admin/lesson-plans/RightSidebar.vue";
import TitleSection from "@/components/admin/lesson-plans/TitleSection.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import lessonPlanService from "@/services/lessonPlanService";
import { useUnsavedChanges } from "@/composeables/useUnsavedChanges";
import { useAdminLoadingOverlay } from "@/composeables/useAdminLayout";
import { onMounted, onUnmounted, ref, inject } from "vue";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();
const rightSidebarOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const editorRef = ref(null);
const rightSidebarRef = ref(null);
const titleSectionRef = ref(null);
const isSubmitting = ref(false);
const savingType = ref(null); // Tracks which save operation is in progress
const validationErrors = ref({});
const showValidationErrors = ref(false);
const isInitialLoading = ref(true);
const showPreviewConfirm = ref(false);

const notify = inject("notify");

// Admin layout integration
const { loadingOverlayProps } = useAdminLoadingOverlay();

// Notification system
const notification = ref({
  show: false,
  message: "",
  type: "success", // success or error
  timeout: null,
});

const showNotification = (message, type = "success") => {
  // Clear any existing timeout
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }

  // Set notification properties
  notification.value.show = true;
  notification.value.message = message;
  notification.value.type = type;

  // Auto-close after 5 seconds
  notification.value.timeout = setTimeout(() => {
    closeNotification();
  }, 5000);
};

const closeNotification = () => {
  notification.value.show = false;
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }
};

// Lesson data
const lessonData = ref({
  id: route.params.id,
  title: "",
  subtitle: "",
  duration: "",
  coverImage: null,
  tagSections: [],
  tabs: [],
  slug: "",
  permalink: "",
  slideUrl: "",
  slidesCount: 0,
  updatedAt: null,
  materials: [],
});

// Initialize unsaved changes tracking
const { markAsChanged, markAsSaved, setInitialLoading } = useUnsavedChanges(
  lessonData,
  {
    confirmMessage:
      "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
  }
);

// Update methods for two-way binding
const updateTitle = (title) => {
  lessonData.value.title = title;
  markAsChanged();
};

const updateSubtitle = (subtitle) => {
  lessonData.value.subtitle = subtitle;
  markAsChanged();
};

const updateDuration = (duration) => {
  lessonData.value.duration = duration;
  markAsChanged();
};

const updateCoverImage = (coverImage) => {
  lessonData.value.coverImage = coverImage;
  markAsChanged();
};

const updateTagSections = (tagSections) => {
  lessonData.value.tagSections = tagSections;
  markAsChanged();
};

const updateTabs = (tabs, materials) => {
  lessonData.value.tabs = tabs;

  if (materials && Array.isArray(materials)) {
    lessonData.value.materials = materials;
  }
  markAsChanged();
};

const updatePermalink = (permalink) => {
  lessonData.value.permalink = permalink;
  markAsChanged();
};

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
};

// Fetch lesson data from API
const fetchLessonData = async () => {
  try {
    setInitialLoading(true);
    isInitialLoading.value = true;

    const response = await lessonPlanService.getLessonById(route.params.id);
    if (response.status === 200) {
      const data = response.data.data;

      // Update basic lesson data
      lessonData.value.id = data.id;
      lessonData.value.title = data.title;
      lessonData.value.subtitle = data.short_description || "";
      lessonData.value.duration = data.duration || "";
      lessonData.value.slug = data.slug;
      lessonData.value.permalink = data.permalink;
      lessonData.value.coverImage = data.thumbnail;
      lessonData.value.slideUrl = data.slide_url || "";
      lessonData.value.updatedAt =
        data.updated_at || data.date || new Date().toLocaleString();
      lessonData.value.lesson_instructional = data.lesson_instructional || [];

      // Try to get slide count from metadata if available
      if (data.meta && data.meta.slides_count) {
        lessonData.value.slidesCount = parseInt(data.meta.slides_count);
      }
      // Default to 0 if not available
      else {
        lessonData.value.slidesCount = 0;
      }

      // Parse content JSON if available
      if (data.content) {
        try {
          lessonData.value.tabs = JSON.parse(data.content);
        } catch (e) {
          console.error("Error parsing lesson content:", e);
          lessonData.value.tabs = [];
        }
      }

      // Process categories into tag sections
      if (data.categories && data.categories.length > 0) {
        // Group terms by category name to consolidate multiple entries of the same category
        const categoriesMap = new Map();

        data.categories.forEach((category) => {
          const existingCategory = categoriesMap.get(category.name);

          if (existingCategory) {
            // Merge terms if this category already exists
            existingCategory.terms = [
              ...existingCategory.terms,
              ...category.terms,
            ];
          } else {
            // Add new category to map
            categoriesMap.set(category.name, { ...category });
          }
        });

        // Transform the categories into the format expected by RightSidebar
        const tagSections = Array.from(categoriesMap.values()).map(
          (category) => {
            return {
              id: category.name,
              title: category.title,
              // Format selected terms properly
              selected: category.terms.map((term) =>
                JSON.stringify({
                  id: term.id,
                  title: term.title,
                })
              ),
            };
          }
        );

        lessonData.value.tagSections = tagSections;
      }

      // Update child components with the data
      updateChildComponents();

      // Mark as saved after loading data and updating child components
      setTimeout(() => {
        markAsSaved();
        setInitialLoading(false);
        isInitialLoading.value = false;
      }, 500);
    }
  } catch (error) {
    console.error("Error fetching lesson data:", error);
    showNotification("Failed to load lesson data. Please try again.", "error");
    setInitialLoading(false);
    isInitialLoading.value = false;
  }
};

// Update all child components with the current data
const updateChildComponents = () => {
  if (titleSectionRef.value) {
    titleSectionRef.value.updateFromParent(
      lessonData.value.title,
      lessonData.value.subtitle,
      lessonData.value.duration
    );
  }

  if (editorRef.value) {
    editorRef.value.updateFromParent(lessonData.value.tabs);

    // If slide_url exists, set the attached slides state
    if (lessonData.value.slideUrl) {
      // Wait for editor to initialize properly
      setTimeout(() => {
        editorRef.value.setSlideAttachedState({
          url: lessonData.value.slideUrl,
          code: null,
        });
      }, 300);
    }

    if (
      lessonData.value.lesson_instructional &&
      lessonData.value.lesson_instructional.length > 0
    ) {
      const materials = lessonData.value.lesson_instructional.map(
        (material) => ({
          id: material.id,
          title: material.name,
          url: material.url,
        })
      );

      editorRef.value.setInstructionalMaterials(materials);
    }
  }

  if (rightSidebarRef.value) {
    rightSidebarRef.value.updateFromParent(
      lessonData.value.coverImage,
      lessonData.value.tagSections,
      lessonData.value.permalink || lessonData.value.slug
    );
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);

  // Fetch lesson data when component mounts
  if (route.params.id) {
    fetchLessonData();
  } else {
    // If no ID is provided, redirect to list page
    isInitialLoading.value = false;
    router.push({ name: "LessonPlansListPage" });
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

function toggleRightSidebar() {
  rightSidebarOpen.value = !rightSidebarOpen.value;
}

// Modal functions for preview confirmation
const showPreviewModal = () => {
  showPreviewConfirm.value = true;
};

const closePreviewModal = () => {
  showPreviewConfirm.value = false;
};

const confirmPreview = async () => {
  showPreviewConfirm.value = false;
  await previewLesson();
};

// Shared function to prepare lesson data for API
const prepareLessonData = async (status = null) => {
  const formData = new FormData();
  formData.append("title", lessonData.value.title);
  formData.append("slug", lessonData.value.permalink || lessonData.value.slug);
  formData.append("content", JSON.stringify(lessonData.value.tabs));
  formData.append("duration", lessonData.value.duration);

  // Use provided status or keep the original status for preview
  const finalStatus = status || lessonData.value.status || "draft";
  formData.append("status", finalStatus);

  formData.append("short_description", lessonData.value.subtitle || "");

  let slideData = null;
  // Add slide URL if slides are attached or if it existed before
  if (
    (editorRef.value && editorRef.value.isSlideAttached()) ||
    lessonData.value.slideUrl
  ) {
    slideData = editorRef.value.getSlideData();
    let slideUrl = lessonData.value.slideUrl;

    if (slideData) {
      slideUrl = slideData.url;
    }

    if (slideUrl) {
      formData.append("slide_url", slideUrl);
    }
  }

  // Process cover image
  if (lessonData.value.coverImage) {
    // If the image is a base64 string, convert it to a file
    if (
      typeof lessonData.value.coverImage === "string" &&
      lessonData.value.coverImage.startsWith("data:image")
    ) {
      const imageBlob = await fetch(lessonData.value.coverImage).then((r) =>
        r.blob()
      );
      const imageFile = new File([imageBlob], `cover-image-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      formData.append("thumbnail", imageFile);
    } else if (typeof lessonData.value.coverImage === "object") {
      // If it's already a file object, just append it
      formData.append("thumbnail", lessonData.value.coverImage);
    }
  }

  // Process categories
  const categories = formatCategories(lessonData.value.tagSections);
  formData.append("categories", JSON.stringify(categories));

  return { formData, slideData };
};

// Shared function to save lesson data
const saveLessonData = async (formData, slideData) => {
  // Call API to update
  await lessonPlanService.updateLesson(lessonData.value.id, formData);

  if (slideData && slideData.code) {
    await lessonPlanService.assignSlideToLesson({
      lesson_id: lessonData.value.id,
      code: slideData.code,
      replace_existing: true,
    });
  }
};

// Preview lesson function (refactored)
async function previewLesson() {
  if (isSubmitting.value) return;

  try {
    // Validate data first
    if (!validateLessonData()) {
      return;
    }

    isSubmitting.value = true;
    savingType.value = "preview";
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare and save data (preserving original status)
    const { formData, slideData } = await prepareLessonData();
    await saveLessonData(formData, slideData);

    notify.success(
      "Lesson plan saved successfully! Redirecting to preview...",
      "Success"
    );

    // Mark as saved after successful save
    markAsSaved();

    // Small delay to show the success message before redirecting
    setTimeout(() => {
      // Navigate to preview page with lesson ID
      router.push({
        name: "LessonPlansPreviewPage",
        params: { id: lessonData.value.id },
      });
    }, 1000);
  } catch (error) {
    console.error("Error saving lesson plan for preview:", error.response);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to save lesson plan for preview. Please try again.";
    notify.error(errorMessage, "Error");
    showValidationErrors.value = true;
    validationErrors.value = {
      api: errorMessage,
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
}

// Format categories for API
const formatCategories = (tagSections) => {
  if (!tagSections || !tagSections.length) return [];

  return tagSections
    .filter(
      (section) =>
        section.id !== "standards" &&
        section.selected &&
        section.selected.length
    )
    .map((section) => {
      const termIds = section.selected
        .map((tag) => {
          try {
            return typeof tag === "object" ? tag.id : JSON.parse(tag).id;
          } catch (e) {
            return null;
          }
        })
        .filter((id) => id !== null);

      return {
        category_id: section.id,
        term_id: termIds,
      };
    });
};

// Validate lesson data
const validateLessonData = () => {
  const errors = {};

  if (!lessonData.value.title.trim()) {
    errors.title = "Title is required";
  }

  if (!lessonData.value.duration) {
    errors.duration = "Duration is required";
  }

  if (!lessonData.value.coverImage) {
    errors.coverImage = "Cover image is required";
  }

  // Add validation for required tag sections
  const requiredSectionTitles = ["Tech Domain", "Topic", "Software", "Grade"];

  const missingSections = lessonData.value.tagSections
    .filter(
      (section) =>
        requiredSectionTitles.includes(section.title) &&
        (!section.selected || section.selected.length === 0)
    )
    .map((section) => section.title);

  if (missingSections.length > 0) {
    errors.tagSections = `Please select at least one tag in each of these required categories: ${missingSections.join(
      ", "
    )}`;
  }

  validationErrors.value = errors;

  // Show errors UI if there are errors
  if (Object.keys(errors).length > 0) {
    showValidationErrors.value = true;
  }

  return Object.keys(errors).length === 0;
};

// Save lesson (refactored)
const saveLesson = async (status) => {
  if (isSubmitting.value) return;

  // Validate data
  if (!validateLessonData()) {
    return;
  }

  try {
    isSubmitting.value = true;
    savingType.value = status;
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare and save data with the specified status
    const { formData, slideData } = await prepareLessonData(status);
    await saveLessonData(formData, slideData);

    const successMessage = `Lesson plan ${
      status === "published" ? "published" : "saved as draft"
    } successfully!`;

    notify.success(successMessage, "Success");
    showNotification(successMessage);

    // Mark as saved after successful save
    markAsSaved();

    // Redirect to list page
    router.push({ name: "LessonPlansListPage" });
  } catch (error) {
    console.error("Error updating lesson plan:", error.response);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to update lesson plan. Please try again.";
    notify.error(errorMessage, "Error");
    showNotification(errorMessage, "error");
    showValidationErrors.value = true;
    validationErrors.value = {
      api: errorMessage,
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
};
</script>

<style scoped>
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.preview-button,
.draft-button,
.publish-button,
.clear-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.preview-button {
  background-color: var(--primary-blue);
  color: white;
}

.preview-button:hover {
  opacity: 0.8;
}

.draft-button {
  background-color: #6c757d;
  color: white;
}

.draft-button:hover {
  background-color: #5a6268;
}

.publish-button {
  background-color: #28a745;
  color: white;
}

.publish-button:hover {
  background-color: #218838;
}

.clear-button {
  background-color: #e74c3c;
  color: white;
}

.clear-button:hover {
  background-color: #c0392b;
}

/* Button disabled state */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner for loading state */
.action-buttons .spinner {
  width: 16px !important;
  height: 16px !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 50% !important;
  border-top-color: #fff !important;
  animation: spin 0.8s linear infinite !important;
  background-color: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: inline-block !important;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Validation errors */
.validation-errors {
  margin-bottom: 20px;
  border: 1px solid #f8d7da;
  border-radius: 4px;
  background-color: #fff5f6;
  overflow: hidden;
}

.error-header {
  display: flex;
  align-items: center;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  font-weight: bold;
  gap: 10px;
}

.error-header h3 {
  margin: 0;
  flex-grow: 1;
}

.close-error {
  background: none;
  border: none;
  color: #721c24;
  font-size: var(--fs-big);
  cursor: pointer;
}

.validation-errors ul {
  margin: 0;
  padding: 15px 15px 15px 40px;
  color: #721c24;
}

.validation-errors li {
  margin-bottom: 5px;
}

/* Snackbar Styles */
.snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 80%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slide-up 0.3s ease-out;
}

.snackbar.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.snackbar.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.snackbar-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  padding: 0;
  margin-left: 15px;
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons {
    margin-top: 10px;
    width: 100%;
    flex-wrap: wrap;
  }

  .preview-button,
  .draft-button,
  .publish-button {
    flex: 1;
    min-width: 120px;
    justify-content: center;
  }

  .snackbar {
    width: 90%;
    bottom: 10px;
  }
}
</style>
